import { inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Intercom as CapIntercom } from '@capacitor-community/intercom';
import Intercom from '@intercom/messenger-js-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  EMPTY,
  Observable,
  catchError,
  distinctUntilChanged,
  filter,
  from,
  map,
  of,
  pairwise,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

import { UserModel } from '@ninety/mobile/platform/api';
import { environment } from '@ninety/mobile/platform/environments';
import { DatadogLogsService } from '@ninety/mobile/platform/services';

import { FeatureFlagKeys } from '../../app-entities/feature-flag/feature-flag-state.model';
import { featureFlagSelectors } from '../../app-entities/feature-flag/feature-flag-state.selectors';
import { UserListStateActions } from '../../app-entities/user-list/user-list-state.actions';
import { SessionActions } from '../session/session.actions';

import { FinApiService } from './fin-api.service';
import { IntercomNativeSessionService } from './intercom-native-session.service';
import { NavMenuIntercomActions } from './intercom-state.actions';

@Injectable()
export class IntercomStateEffects {
  private actions$ = inject(Actions);
  private readonly store = inject(Store);

  private isNative = Capacitor.isNativePlatform();
  private readonly finApiService = inject(FinApiService);
  private readonly nativeSession = inject(IntercomNativeSessionService);
  private readonly logs = inject(DatadogLogsService);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly window: any = window;

  /**
   * On login, fetch the Fin token (a short-lived JWT) and hand it to Intercom to satisfy Messenger
   * Security: `setUserJwt` on native, `intercom_user_jwt` on web. A fetch failure must never break login.
   */
  registerIntercom$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserListStateActions.setLoggedInUser),
        switchMap(({ loggedInUser }) =>
          this.finApiService.getFinToken().pipe(
            // Scoped to the token fetch — never break login. register*() handle their own errors.
            catchError(error => {
              this.logs.error('Failed to fetch Intercom Fin token', undefined, error);
              // Can't re-register without a token, so shut down rather than leave the prior user
              // identified on a shared device.
              this.shutdown();
              return EMPTY;
            }),
            switchMap(({ access_token }) => this.register(loggedInUser, access_token)),
            // A late token completion after logout would re-boot Intercom for the logged-out session,
            // after shutdownIntercom$ already tore it down.
            takeUntil(this.actions$.pipe(ofType(SessionActions.signedOut)))
          )
        )
      ),
    { dispatch: false }
  );

  showIntercomMessenger$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NavMenuIntercomActions.showMessenger, NavMenuIntercomActions.showIntercom),
        tap(async () => {
          if (this.isNative) {
            try {
              await CapIntercom.displayMessenger();
            } catch (error) {
              this.logs.info('Intercom displayMessenger failed', undefined, error as Error);
            }
            return;
          }
          this._intercom('show');
        })
      ),
    { dispatch: false }
  );

  hideIntercomMessenger$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NavMenuIntercomActions.hideIntercom),
        tap(async () => {
          if (this.isNative) {
            try {
              await CapIntercom.hideMessenger();
              await CapIntercom.hideLauncher();
            } catch (error) {
              this.logs.info('Intercom hideMessenger failed', undefined, error as Error);
            }
            return;
          }
          this._intercom('hide');
        })
      ),
    { dispatch: false }
  );

  /**
   * Sign-out teardown, keyed on the `notifyAppSignedOut` command, not the derived `signedOut` action:
   * the command is dispatched before `navigate→signIn`, so this logout enqueues ahead of the launcher's
   * anon registration. Keying on `signedOut` would enqueue after it and hide the launcher.
   */
  shutdownIntercom$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SessionActions.commandIssued),
        filter(({ command }) => command.do === 'notifyAppSignedOut'),
        tap(() => this.shutdown())
      ),
    { dispatch: false }
  );

  /**
   * Native only: drive the login-screen launcher off the flag while signIn is the destination. Keyed
   * on `navigate→signIn`, not the signed-out state (which advances before the sign-out logout and would
   * race registration ahead of it). `startAnonymousSession` is marker-idempotent, so off→on→off→on resumes.
   */
  showAnonymousLauncher$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SessionActions.commandIssued),
        // not Keychain auto-login, so no launcher flash on resume
        filter(({ command }) => command.do === 'navigate' && command.to === 'signIn' && this.isNative),
        switchMap(() =>
          this.store.select(featureFlagSelectors.selectAll).pipe(
            // Ignore the prior user's ready flags while sign-out re-anonymizes LaunchDarkly.
            filter(flags => flags.flagsContext === 'anonymous'),
            map(flags => flags[FeatureFlagKeys.intercomLoginLauncher] === true),
            distinctUntilChanged(),
            startWith(false),
            pairwise(),
            filter(([wasEnabled, isEnabled]) => wasEnabled !== isEnabled),
            takeUntil(this.actions$.pipe(ofType(UserListStateActions.setLoggedInUser))), // end on login
            tap(([, isEnabled]) => {
              if (isEnabled) {
                this.nativeSession.startAnonymousSession();
              } else {
                this.nativeSession.hideLauncher();
              }
            })
          )
        )
      ),
    { dispatch: false }
  );

  // Re-assert the hidden launcher on nav-menu messenger open/close. These all fire after boot, so the
  // `update` merge has a booted widget; getLoginPersonSuccess / reloadApp were removed because they can
  // fire before boot, which updates an uninitialised widget ("App ID not set" + an anonymous 403 ping).
  hideDefaultLauncher$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          NavMenuIntercomActions.hideIntercom,
          NavMenuIntercomActions.showMessenger,
          NavMenuIntercomActions.showIntercom
        ),
        tap(() => {
          // The native SDK owns the messenger on native; only touch the web widget on web/PWA.
          // Use `update` (a merge), not the `Intercom({...})` initializer, which would overwrite
          // window.intercomSettings wholesale and drop the Fin JWT set at boot.
          if (!this.isNative) {
            this._intercom('update', { hide_default_launcher: true });
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * Delete every `intercom-` cookie on logout: `shutdown` leaves the persistent `intercom-id-*`
   * (anonymous_id), which carries one user's identity into the next login on a shared browser. Scope is
   * unknown, so expire each name across host-only + every parent domain (an unsettable `domain=` is ignored).
   */
  private clearIntercomCookies(): void {
    const doc: Document = this.window.document;
    const host = this.window.location?.hostname ?? '';
    const names = (doc.cookie ?? '')
      .split(';')
      .map(cookie => cookie.split('=')[0].trim())
      .filter(name => name.startsWith('intercom-'));
    for (const name of names) {
      for (const domain of this.cookieDomainScopes(host)) {
        doc.cookie = `${name}=; path=/; max-age=0${domain ? `; domain=${domain}` : ''}`;
      }
    }
  }

  /** Host-only (`''`) + each parent domain (dot-prefixed) down to the registrable domain. */
  private cookieDomainScopes(host: string): string[] {
    const scopes = ['']; // no domain attr → deletes host-only cookies
    const parts = host.split('.');
    for (let i = 0; i < parts.length - 1; i++) {
      scopes.push(`.${parts.slice(i).join('.')}`);
    }
    return scopes;
  }

  private register(loggedInUser: UserModel, accessToken: string): Observable<void> {
    return this.isNative ? this.registerNative(loggedInUser, accessToken) : this.registerWeb(accessToken);
  }

  /** Native: identify through the serialized session owner. See {@link IntercomNativeSessionService}. */
  private registerNative(user: UserModel, accessToken: string): Observable<void> {
    const { personId, primaryEmail } = user;
    return from(this.nativeSession.registerIdentified(personId, primaryEmail, accessToken));
  }

  /**
   * Web: boot with the JWT so the session is identified from the first ping. An anonymous boot leaves
   * an `intercom-id` (anonymous_id) cookie that survives logout and bleeds history into the next user
   * on a shared device. Identity is in the JWT claims; passing user_id explicitly triggers a mismatch.
   */
  private registerWeb(accessToken: string): Observable<void> {
    return of(undefined).pipe(
      tap(() =>
        Intercom({
          app_id: environment.INTERCOM_APP_ID,
          intercom_user_jwt: accessToken,
          hide_default_launcher: true,
        })
      ),
      catchError(error => {
        this.logs.error('Intercom web boot failed', undefined, error);
        return EMPTY;
      })
    );
  }

  shutdown(): void {
    if (this.isNative) {
      // Through the serialized owner so it is ordered against any anon registration and clears the
      // marker. The owner logs and swallows its own failures.
      void this.nativeSession.logout();
    } else {
      this.shutdownWeb();
    }
  }

  /**
   * Reference: https://developers.intercom.com/installing-intercom/web/installation#how-to-shut-down-intercom
   */
  private shutdownWeb() {
    try {
      // shutdown() alone leaves the `intercom-id` (anonymous_id) cookie, which would bleed this
      // session's history into the next user on a shared device — delete the intercom- cookies too.
      this._intercom('shutdown');
      this.clearIntercomCookies();
    } catch (error) {
      this.logs.info('Intercom shutdown failed', undefined, error as Error);
    }
  }

  /** More Info: https://developers.intercom.com/installing-intercom/web/attributes-objects/ */
  private _intercom(action: string, config?: Record<string, boolean | string | number>) {
    try {
      if (!config) {
        this.window.Intercom(action);
      } else {
        this.window.Intercom(action, config);
      }
    } catch (error) {
      this.logs.info('Intercom action failed', { action }, error as Error);
    }
  }
}
